import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  Box, Button, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, ListItemIcon, Menu, MenuItem, Typography,
} from "@mui/material";
import { AddOutlined, DeleteOutlined, MoreVert, NewspaperOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import Loading from "../../../components/Loading";
import CardPublicacion, { MEDIA_MOBILE } from "../../../components/publicaciones-home/CardPublicacion";
import EditorPublicacion from "../../../components/publicacion/EditorPublicacion";
import { api } from "../../../services/api";
import withAuthenticationRequired from "../../../components/ProtectedRoutes/withAuthenticationRequired";
import useEventoStore from "../../../stores/useEventoStore";
import { fromPublicacion } from "../../../domain/publicacion/payload";
import { ROL } from "../../../domain/staff/roles";
import { PADDING_COMO_HEADER } from "../../../components/Header";

const PRIMARIO = "#5656FF";
const FUENTE = "Bricolage Grotesque";

const estadoDe = (pub) => {
  const ahora = moment();
  if (ahora.isBefore(pub.desde)) return { texto: "Programada", color: "#2563eb", fondo: "#eff6ff" };
  if (ahora.isAfter(pub.hasta)) return { texto: "Finalizada", color: "#6b7280", fondo: "#f3f4f6" };
  if (moment(pub.hasta).diff(ahora, "days") < 7) return { texto: "Últimos días", color: "#b45309", fondo: "#fef3c7" };
  return { texto: "Activa", color: "#15803d", fondo: "#dcfce7" };
};

const Estado = ({ pub }) => {
  const estado = estadoDe(pub);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.5,
        borderRadius: 5, bgcolor: estado.fondo, color: estado.color, fontSize: 13, fontWeight: 600,
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: estado.color }} />
      {estado.texto}
    </Box>
  );
};

const Publicacion = ({ pub, evento, esOwner, onGuardada, onEliminar }) => {
  const [editando, setEditando] = useState(false);
  const [menu, setMenu] = useState(null);
  const mobile = useMediaQuery({ query: MEDIA_MOBILE });
  // memorizado: el editor resetea el formulario si cambia la referencia
  const valores = useMemo(() => fromPublicacion(pub), [pub]);

  const guardar = async (payload) => {
    try {
      const response = await api.publicaciones.updatePublicacion(payload);
      if (response.status !== "success") {
        toast.error(response.message || "No se pudo guardar la publicación.");
        return;
      }
      toast.success("Los cambios se guardaron correctamente.");
      setEditando(false);
      onGuardada();
    } catch (err) {
      toast.error("No se pudo conectar con el servidor.");
    }
  };

  return (
    <Box
      sx={{
        width: "fit-content",
        maxWidth: "100%",
        mx: "auto",
        mb: 3,
        "& .contenedor-card, & .contenedor-card-normal": {
          marginLeft: "0 !important",
          marginRight: "0 !important",
          marginTop: editando ? "32px !important" : mobile ? "16px !important" : "8px !important",
        },
      }}
    >
      {!editando && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
          <Estado pub={pub} />
          {esOwner && (
            <>
              <IconButton size="small" aria-label="Más acciones" onClick={(e) => setMenu(e.currentTarget)}>
                <MoreVert />
              </IconButton>
              <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
                <MenuItem
                  onClick={() => {
                    setMenu(null);
                    onEliminar(pub);
                  }}
                  sx={{ color: "#ef4444" }}
                >
                  <ListItemIcon><DeleteOutlined fontSize="small" sx={{ color: "#ef4444" }} /></ListItemIcon>
                  Eliminar
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      )}

      {editando ? (
        <EditorPublicacion
          evento={evento}
          eventoId={pub.idevento}
          valoresIniciales={valores}
          esNueva={false}
          onSubmit={guardar}
          textoSubmit="Guardar cambios"
          periodoArriba
          accesorioPeriodo={<Estado pub={pub} />}
        >
          <Button onClick={() => setEditando(false)} sx={{ textTransform: "none", color: "#374151" }}>
            Cancelar
          </Button>
        </EditorPublicacion>
      ) : (
        <CardPublicacion
          publicacion={pub}
          variant={mobile ? "normal" : "solo"}
          onEditar={esOwner ? () => setEditando(true) : undefined}
        />
      )}
    </Box>
  );
};

const Publicaciones = () => {
  const history = useHistory();
  const { id: eventoId } = useParams();
  const userRole = useEventoStore(
    (state) => state.eventosPorId[eventoId]?.data?.eventoUsuario?.idrol ?? null
  );
  const evento = useEventoStore((state) => state.eventosPorId[eventoId]?.data?.evento ?? null);
  const esOwner = userRole === ROL.OWNER;

  const [publicaciones, setPublicaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const response = await api.publicaciones.getAll(eventoId, "", 10, 1);
      setPublicaciones(response.status === "success" ? response.data?.publicaciones ?? [] : []);
    } catch (error) {
      toast.error("No se pudo cargar la publicación.");
    } finally {
      setCargando(false);
    }
  }, [eventoId]);

  useEffect(() => {
    useEventoStore.getState().fetchEvento(eventoId);
    cargar();
  }, [eventoId, cargar]);

  const eliminar = async () => {
    setEliminando(true);
    try {
      const response = await api.publicaciones.deletePublicacion(aEliminar.id);
      if (response.status !== "success") {
        toast.error(response.message || "No se pudo eliminar la publicación.");
        return;
      }
      toast.success("Publicación eliminada");
      setAEliminar(null);
      cargar();
    } catch (error) {
      toast.error("No se pudo conectar con el servidor.");
    } finally {
      setEliminando(false);
    }
  };

  if (cargando) return <Loading />;

  return (
    <Container maxWidth="md" className="mt-3 mb-5" sx={PADDING_COMO_HEADER}>
      {!cargando && publicaciones.length === 0 && (
        <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f0f0f0", py: 5, px: 2, textAlign: "center" }}>
          <NewspaperOutlined sx={{ fontSize: 40, color: "#d1d5db", mb: 1.5 }} />
          <Typography sx={{ color: "text.secondary", fontSize: 14, mb: esOwner ? 2 : 0 }}>
            {esOwner
              ? "Este evento todavía no tiene publicación. Creala para que aparezca en el inicio."
              : "Este evento todavía no tiene publicación."}
          </Typography>
          {esOwner && (
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => history.push(`/eventos/${eventoId}/publicaciones/nueva`)}
              sx={{ bgcolor: PRIMARIO, textTransform: "none", fontFamily: FUENTE, "&:hover": { bgcolor: "#4545ee" } }}
            >
              Crear publicación
            </Button>
          )}
        </Box>
      )}

      {!cargando && publicaciones.map((pub) => (
        <Publicacion
          key={pub.id}
          pub={pub}
          evento={evento}
          esOwner={esOwner}
          onGuardada={cargar}
          onEliminar={setAEliminar}
        />
      ))}

      <Dialog open={Boolean(aEliminar)} onClose={() => !eliminando && setAEliminar(null)}>
        <DialogTitle sx={{ fontFamily: FUENTE, fontSize: 18 }}>¿Eliminar publicación?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{aEliminar?.titulo}</strong> deja de mostrarse en el inicio y se borran sus imágenes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAEliminar(null)} disabled={eliminando} sx={{ textTransform: "none", color: "#374151" }}>
            Cancelar
          </Button>
          <Button variant="outlined" color="error" onClick={eliminar} disabled={eliminando} sx={{ textTransform: "none" }}>
            {eliminando ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default withAuthenticationRequired(Publicaciones, {
  onRedirecting: () => <Loading />,
});
